<template>
    <div class="buffon-simulation">
      <h2>Симуляция Бюффона для вычисления числа π</h2>
      
      <div class="canvas-container">
        <canvas 
          ref="canvas" 
          :width="canvasWidth" 
          :height="canvasHeight"
          class="simulation-canvas"
        ></canvas>
      </div>
      
      <div class="controls">
        <div class="parameters">
          <label>
            Длина иглы:
            <input type="number" v-model.number="needle" step="0.1" min="0.1" max="0.9">
          </label>
          <span class="value">{{ needle.toFixed(1) }}</span>
        </div>
        
        <div class="parameters">
          <label>
            Расстояние между линиями:
            <input type="number" v-model.number="width" step="0.1" min="0.5" max="2.0">
          </label>
          <span class="value">{{ width.toFixed(1) }}</span>
        </div>
        
        <div class="buttons">
          <button @click="runSimulation" :disabled="isRunning">
            {{ isRunning ? 'Симуляция...' : 'Запустить симуляцию' }}
          </button>
          <button @click="resetSimulation" :disabled="isRunning">
            Сбросить
          </button>
        </div>
      </div>
      
      <div class="results" v-if="totalThrows > 0">
        <h3>Результаты:</h3>
        <table>
          <tr>
            <td>Всего бросков:</td>
            <td>{{ totalThrows }}</td>
          </tr>
          <tr>
            <td>Пересечений:</td>
            <td>{{ numHits }}</td>
          </tr>
          <tr>
            <td>Промахов:</td>
            <td>{{ numMisses }}</td>
          </tr>
          <tr>
            <td>Вероятность пересечения:</td>
            <td>{{ probability.toFixed(4) }}</td>
          </tr>
          <tr>
            <td>Вычисленное π:</td>
            <td><strong>{{ piEstimate.toFixed(4) }}</strong></td>
          </tr>
          <tr>
            <td>Погрешность:</td>
            <td>{{ error.toFixed(4) }} ({{ errorPercent.toFixed(2) }}%)</td>
          </tr>
        </table>
      </div>
    </div>
  </template>
  
  <script>
  
  export default {
    name: 'BuffonSimulation',
    
    data() {
      return {
        // Параметры симуляции
        width: 1.0,
        needle: 0.5,
        numHits: 0,
        numMisses: 0,
        
        // Параметры канваса
        canvasWidth: 800,
        canvasHeight: 400,
        
        // Состояние симуляции
        isRunning: false,
        
        // Данные для отрисовки
        needles: [],
        lines: [0, 1, 2, 3]
      }
    },
    
    computed: {
      totalThrows() {
        return this.numHits + this.numMisses
      },
      
      probability() {
        return this.totalThrows > 0 ? this.numHits / this.totalThrows : 0
      },
      
      piEstimate() {
        return this.probability > 0 
          ? (2 * this.needle) / (this.probability * this.width)
          : 0
      },
      
      error() {
        return Math.abs(Math.PI - this.piEstimate)
      },
      
      errorPercent() {
        return (this.error / Math.PI) * 100
      }
    },
    
    watch: {
      width() {
        this.drawBackground()
        this.redrawNeedles()
      },
      
      needle() {
        this.redrawNeedles()
      }
    },
    
    mounted() {
      this.initCanvas()
      this.drawBackground()
    },
    
    methods: {
      initCanvas() {
        const canvas = this.$refs.canvas
        const ctx = canvas.getContext('2d')
        this.ctx = ctx
      },
      
      drawBackground() {
        if (!this.ctx) return
        
        const ctx = this.ctx
        const canvas = this.$refs.canvas
        
        // Очищаем канвас
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        
        // Рисуем линии
        ctx.strokeStyle = 'white'
        ctx.lineWidth = 3
        
        for (let i = 0; i <= 3; i++) {
          const x = i * (canvas.width / 3)
          ctx.beginPath()
          ctx.moveTo(x, 0)
          ctx.lineTo(x, canvas.height)
          ctx.stroke()
          
          // Подписываем линии
          ctx.fillStyle = '#333'
          ctx.font = '12px Arial'
          ctx.fillText(`x = ${i}`, x + 5, 20)
        }
      },
      
      drawNeedle(needle) {
        const ctx = this.ctx
        const canvas = this.$refs.canvas
        
        const scaleX = canvas.width / 3
        const scaleY = canvas.height / 4
        
        const x1 = needle.x * scaleX
        const y1 = needle.y * scaleY
        const x2 = needle.xx * scaleX
        const y2 = needle.yy * scaleY
        
        ctx.beginPath()
        ctx.moveTo(x1, y1)
        ctx.lineTo(x2, y2)
        ctx.strokeStyle = needle.isHit ? '#ff4444' : '#44ff44'
        ctx.lineWidth = 2
        ctx.stroke()
        
        // Рисуем кружочки на концах иглы
        ctx.beginPath()
        ctx.arc(x1, y1, 3, 0, 2 * Math.PI)
        ctx.fillStyle = needle.isHit ? '#ff0000' : '#00ff00'
        ctx.fill()
        
        ctx.beginPath()
        ctx.arc(x2, y2, 3, 0, 2 * Math.PI)
        ctx.fillStyle = needle.isHit ? '#ff0000' : '#00ff00'
        ctx.fill()
      },
      
      redrawNeedles() {
        this.drawBackground()
        this.needles.forEach(needle => this.drawNeedle(needle))
      },
      
      checkIntersection(x, xx) {
        // Проверяем пересечение с линиями x = 0, 1, 2, 3
        for (let line of this.lines) {
          if ((x < line && xx > line) || (x > line && xx < line)) {
            return true
          }
        }
        return false
      },
      
      async runSimulation() {
        if (this.isRunning) return
        
        this.isRunning = true
        this.numHits = 0
        this.numMisses = 0
        this.needles = []
        
        const iterations = 1000
        const batchSize = 10
        
        for (let i = 0; i < iterations; i += batchSize) {
          // Обрабатываем пачку бросков
          for (let j = 0; j < batchSize && i + j < iterations; j++) {
            // Генерируем случайные координаты
            const x = 3 * Math.random()
            const y = 4 * Math.random()
            
            // Генерируем случайный угол
            const angle = (2 * Math.PI) * Math.random()
            
            // Вычисляем конечные координаты
            let xx = x + this.needle * Math.cos(angle)
            let yy = y + this.needle * Math.sin(angle)
            
            // Проверяем пересечение
            const isHit = this.checkIntersection(x, xx)
            
            // Сохраняем иглу для отрисовки
            this.needles.push({
              x, y, xx, yy,
              isHit
            })
            
            // Обновляем счетчики
            if (isHit) {
              this.numHits++
            } else {
              this.numMisses++
            }
          }
          
          // Обновляем отрисовку
          this.redrawNeedles()
          
          // Даем браузеру время на отрисовку
          await new Promise(resolve => setTimeout(resolve, 10))
        }
        
        this.isRunning = false
      },
      
      resetSimulation() {
        this.numHits = 0
        this.numMisses = 0
        this.needles = []
        this.drawBackground()
      }
    }
  }
  </script>
  
  <style scoped>
  .buffon-simulation {
    max-width: 900px;
    margin: 0 auto;
    padding: 20px;
    font-family: Arial, sans-serif;
  }
  
  .canvas-container {
    border: 1px solid #ffffff;
    margin-bottom: 20px;
    background-color: #ffffff;
  }
  
  .simulation-canvas {
    display: block;
    width: 100%;
    height: auto;
  }
  
  .controls {
    margin-bottom: 20px;
    padding: 15px;
    background-color: #e6e3e3;
    border-radius: 5px;
  }
  
  .parameters {
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  
  .parameters label {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  
  .parameters input {
    width: 80px;
    padding: 5px;
    border: 1px solid #ffffff;
    border-radius: 3px;
  }
  
  .parameters .value {
    min-width: 40px;
    text-align: right;
    font-weight: bold;
  }
  
  .buttons {
    display: flex;
    gap: 10px;
    margin-top: 15px;
  }
  
  button {
    padding: 10px 20px;
    background-color: #4CAF50;
    color: white;
    border: none;
    border-radius: 3px;
    cursor: pointer;
    font-size: 14px;
  }
  
  button:hover:not(:disabled) {
    background-color: #45a049;
  }
  
  button:disabled {
    background-color: #ffffff;
    cursor: not-allowed;
  }
  
  button:last-child {
    background-color: #f44336;
  }
  
  button:last-child:hover:not(:disabled) {
    background-color: #da190b;
  }
  
  .results {
    padding: 15px;
    background-color: #e8f4f8;
    border-radius: 5px;
  }
  
  .results h3 {
    margin-top: 0;
    color: #333;
  }
  
  .results table {
    width: 100%;
    border-collapse: collapse;
  }
  
  .results td {
    padding: 8px;
    border-bottom: 1px solid #ddd;
  }
  
  .results td:first-child {
    font-weight: bold;
    width: 200px;
  }
  
  .results td:last-child {
    text-align: right;
  }
  
  .results strong {
    color: #2196F3;
    font-size: 1.2em;
  }
  </style>